import moment, { Moment } from 'moment';
import { Box, Button, Typography, Checkbox, FormControlLabel, CircularProgress, useMediaQuery, createFilterOptions, Grid } from "@mui/material";
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { blue, grey } from "@mui/material/colors";
import { Link, useNavigate } from "react-router-dom";
import styles from "../../assets/styles/form.module.css";
import { useCallback, useEffect, useState } from "react";
import { PinnacleTextField } from "../../components/PinnacleTextField";
import getTheme from "../../theme/theme";
import { PinnacleThemes } from "../../theme/types";
import { register } from "../../api/services/auth";
import { E_ENTER_PASSWORD, PREBOARD_LOGO_WITH_TEXT_URL, E_CONFIRM_PASSWORD, E_PASSWORD_NOT_MATCHED, E_REQUIRED_BIRTHDATE, PREBOARD_AUTH_BANNER_DESKTOP_URL, PREBOARD_AUTH_BANNER_MOBILE_URL } from "../../utils/constants";
import PasswordAdornment from "../../components/common/PasswordAdornment/PasswordAdornment";
import { NotificationTypes } from "../../components/PinnacleSnackbarProvider/PinnacleSnackbarProvider.types";
import { pushNotification } from "../../components/PinnacleSnackbarProvider/PinnacleSnackbarProvider";
import { handleApiError } from "../../utils/errorHandler";
import { School } from '../../api/interfaces/School';
import { getSchools } from '../../api/services/school';
import PinnacleAutocomplete from '../../components/PinnacleAutocomplete/PinnacleAutocomplete';
import { StyledPinnacleDatePicker } from '../../components/PinnacleDatePicker/PinnacleDatePicker.styled';
import { firstTimeLogin } from '../../api/services/user';

export default function FirstTimeLoginPage() {
    const theme = getTheme(PinnacleThemes.BASE_THEME);
    const navigate = useNavigate();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [schoolsLoading, setSchoolsLoading] = useState(true);
    const [schoolsOption, setSchoolsOption] = useState<null | School[]>(null);

    const [formDisabled, setFormDisabled] = useState<boolean>(false);

    // Suffix
    const [suffix, setSuffix] = useState('');
    const [suffixTouched, setSuffixTouched] = useState(false);

    // Birthdate
    const [birthDate, setBirthDate] = useState<Moment | null>(null);
    const [birthDateTouched, setBirthDateTouched] = useState(false);
    const isBirthDateValid = !!birthDate;

    // Calculate max date: today minus 5 years
    const maxAllowedDate = moment().subtract(5, 'years');

    // School
    const [school, setSchool] = useState('');
    const [schoolTouched, setSchoolTouched] = useState(false);

    // Password
    const [password, setPassword] = useState<string>('');
    const [showPassword, setShowPassword] = useState<boolean>(false);
    const [passwordTouched, setPasswordTouched] = useState<boolean>(false);
    const isPasswordValid = password.length > 0;

    // Confirm Password
    const [confirmPassword, setConfirmPassword] = useState<string>('');
    const [showConfirmPassword, setShowConfirmPassword] = useState<boolean>(false);
    const [confirmPasswordTouched, setConfirmPasswordTouched] = useState<boolean>(false);
    const isConfirmPasswordValid = confirmPassword.length > 0;

    const fetchSchool = useCallback(async () => {
        setSchoolsLoading(true);
        try {
            const response = await getSchools();
            setSchoolsOption(response.data)
        } catch (error) {
            handleApiError(error, {
                notify: (msg) => {
                    pushNotification({
                        details: msg,
                        type: NotificationTypes.Error,
                    });
                },
            });
        } finally {
            setSchoolsLoading(false);
        }
    }, []);

    useEffect(() => {
        const init = async () => {
            await fetchSchool();
        }
        init();
    }, [fetchSchool]);


    const handleSuffixChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSuffix(e.target.value);
        if (!suffixTouched) setSuffixTouched(true);
    };

    const handleBirthDateChange = (newValue: Moment | null) => {
        setBirthDate(newValue);
        if (!birthDateTouched) setBirthDateTouched(true);
    };

    const handleSchoolChange = (
        _: React.SyntheticEvent,
        newValue: School | string | null
    ) => {
        if (typeof newValue === 'string') {
            setSchool(newValue);
        } else {
            setSchool(newValue?.name ?? '');
        }
        if (!schoolTouched) setSchoolTouched(true);
    };

    const handleTogglePassword = () => setShowPassword((prev) => !prev);
    const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setPassword(e.target.value);
        if (!passwordTouched) setPasswordTouched(true);
    };

    const handleToggleConfirmPassword = () => setShowConfirmPassword((prev) => !prev);
    const handleConfirmPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setConfirmPassword(e.target.value);
        if (!confirmPasswordTouched) setConfirmPasswordTouched(true);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSuffixTouched(true);
        setBirthDateTouched(true);
        setSchoolTouched(true);
        setPasswordTouched(true);
        setConfirmPasswordTouched(true);

        if (!school) {
            pushNotification({
                details: 'School is required',
                type: NotificationTypes.Error,
            });
            return;
        }

        // Early return if invalid, for clarity
        if (!isPasswordValid || !isBirthDateValid || !isPasswordValid || !isConfirmPasswordValid) {
            return;
        }

        setFormDisabled(true);

        try {
            const registerResponse = await firstTimeLogin({
                suffix,
                birthDate: birthDate.format("YYYY-MM-DD"),
                school,
                password
            });
            pushNotification({
                details: registerResponse.message,
                type: NotificationTypes.Success,
            });
            navigate("/");
        } catch (error) {
            const { errorMessage } = handleApiError(error);
            pushNotification({
                details: errorMessage,
                type: NotificationTypes.Error,
            });
        } finally {
            setFormDisabled(false);
        }
    };

    const filter = createFilterOptions<any>();

    return (
        <>

            <Grid container sx={{ minHeight: '100vh', flexDirection: { xs: 'column', md: 'row' } }}>
                {/* Left image panel */}
                <Grid
                    size={{
                        xs: 12,
                        md: 5
                    }}
                    sx={{
                        height: { xs: '200px', md: 'auto' },
                        overflow: "hidden",
                        backgroundImage: {
                            xs: `url(${PREBOARD_AUTH_BANNER_MOBILE_URL})`,
                            md: `url(${PREBOARD_AUTH_BANNER_DESKTOP_URL})`
                        },
                        backgroundRepeat: "no-repeat",
                        backgroundSize: { xs: 'cover', md: 'contain' },
                        backgroundPosition: "left top",
                        backgroundColor: "#fff",
                    }}
                />

                {/* Right form panel */}
                <Grid
                    size={{
                        xs: 12,
                        md: 7
                    }}
                    sx={{
                        display: 'flex',
                        justifyContent: { xs: 'center', md: 'flex-start' },
                        alignItems: 'center',
                        placeSelf: 'center',
                        overflowY: 'auto',
                        p: { xs: 2, md: 5 },
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: { xs: 'center', md: 'flex-start' },
                            alignItems: { xs: 'center', md: 'flex-start' },
                            width: '100%',
                            minHeight: '100%',
                        }}
                    >
                        <Box
                            sx={{
                                maxWidth: "450px",
                                minWidth: "300px",
                                width: "90%",
                                borderRadius: "5px",
                                boxSizing: "border-box",
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 3,
                            }}
                        >
                            <img
                                src={PREBOARD_LOGO_WITH_TEXT_URL}
                                alt='pinnacle-logo'
                                style={{
                                    width: "300px",
                                    height: "auto",
                                    alignSelf: "left",
                                }}
                            />
                            <Typography variant="h3" gutterBottom sx={{ mt: 3 }}>
                                First-time Login
                            </Typography>
                            <LocalizationProvider dateAdapter={AdapterMoment}>
                                <form onSubmit={handleSubmit} noValidate>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                        {/* SUFFIX */}
                                        <PinnacleTextField
                                            id="suffix"
                                            label="Suffix"
                                            variant="outlined"
                                            autoFocus
                                            onChange={handleSuffixChange}
                                            onBlur={() => setSuffixTouched(true)}
                                            value={suffix}
                                            autoComplete="first-name"
                                            disabled={formDisabled}
                                        />
                                        {/* BIRTHDATE */}
                                        <StyledPinnacleDatePicker
                                            label="Birth Date"
                                            value={birthDate}
                                            onChange={(newValue) => handleBirthDateChange(newValue)}
                                            slots={{
                                                openPickerIcon: () => <CalendarMonthIcon sx={{ color: "#818181" }} />,
                                            }}
                                            slotProps={{
                                                textField: {
                                                    variant: 'outlined',
                                                    required: true,
                                                    error: birthDateTouched && !isBirthDateValid,
                                                    helperText: birthDateTouched && !isBirthDateValid ? E_REQUIRED_BIRTHDATE : '',
                                                    onBlur: () => setBirthDateTouched(true),
                                                    disabled: formDisabled,
                                                    fullWidth: true
                                                },
                                            }}
                                            maxDate={maxAllowedDate}
                                            disableFuture
                                        />

                                        <PinnacleAutocomplete
                                            id="school"
                                            label="School"
                                            options={schoolsOption ?? []}
                                            isOptionEqualToValue={(option, value) => option._id === value._id}
                                            disabled={formDisabled}
                                            placeholder="Choose your school"
                                            renderInput={(params) => (
                                                <PinnacleTextField
                                                    autoComplete="off"
                                                    {...params}
                                                    label="School"
                                                    variant="outlined"
                                                    name="school-field"
                                                    type="text"
                                                    onBlur={() => setSchoolTouched(true)}
                                                    value={school}
                                                    slotProps={{
                                                        htmlInput: { autoComplete: 'new-school' }
                                                    }}
                                                />
                                            )}
                                            onChange={(_, newValue) => {
                                                if (typeof newValue === 'string') {
                                                    handleSchoolChange(_, newValue)
                                                } else if (newValue) {
                                                    // Create a new value from the user input
                                                    handleSchoolChange(_, newValue)
                                                } else {
                                                    handleSchoolChange(_, newValue)
                                                }
                                            }}
                                            filterOptions={(options: any, params: any) => {
                                                const filtered = filter(options, params);

                                                const { inputValue } = params;
                                                // Suggest the creation of a new value
                                                const isExisting = options.some((option: any) => inputValue === option.name);
                                                if (inputValue !== '' && !isExisting) {
                                                    filtered.push({
                                                        inputValue,
                                                        name: inputValue
                                                    });
                                                }

                                                return filtered;
                                            }}
                                            getOptionLabel={(option: any) => {
                                                // Value selected with enter, right from the input
                                                if (typeof option === 'string') {
                                                    return option;
                                                }
                                                // Add "xxx" option created dynamically
                                                if (option.inputValue) {
                                                    return option.inputValue;
                                                }
                                                // Regular option
                                                return option.name ?? '';
                                            }}
                                            renderOption={(props, option) => {
                                                const { key, ...optionProps } = props;
                                                return (
                                                    <li key={key} {...optionProps}>
                                                        {option.name}
                                                    </li>
                                                );
                                            }}
                                        />

                                        <PinnacleTextField
                                            error={passwordTouched && (!isPasswordValid)}
                                            color={passwordTouched && (!isPasswordValid) ? 'error' : 'primary'}
                                            helperText={(passwordTouched && !isPasswordValid) && E_ENTER_PASSWORD}
                                            id="create-password"
                                            label="Password"
                                            variant="outlined"
                                            name="create-password"
                                            onChange={handlePasswordChange}
                                            onBlur={() => setPasswordTouched(true)}
                                            value={password}
                                            type={showPassword ? 'text' : 'password'}
                                            autoComplete="off"
                                            disabled={formDisabled}
                                            slotProps={{
                                                input: {
                                                    endAdornment: <PasswordAdornment show={showPassword} toggleShow={handleTogglePassword} />,
                                                },
                                            }}
                                        />

                                        <PinnacleTextField
                                            error={
                                                (confirmPasswordTouched && (!isConfirmPasswordValid)) ||
                                                password !== confirmPassword
                                            }
                                            color={
                                                ((confirmPasswordTouched && (!isConfirmPasswordValid)) ||
                                                    password !== confirmPassword)
                                                    ? 'error'
                                                    : 'primary'
                                            }
                                            helperText={
                                                (confirmPasswordTouched && !isConfirmPasswordValid)
                                                    ? E_CONFIRM_PASSWORD
                                                    : (password !== confirmPassword)
                                                        ? E_PASSWORD_NOT_MATCHED
                                                        : ''
                                            }
                                            id="confirmNewPassword"
                                            label="Confirm New Password"
                                            variant="outlined"
                                            onChange={handleConfirmPasswordChange}
                                            onBlur={() => setConfirmPasswordTouched(true)}
                                            value={confirmPassword}
                                            type={showConfirmPassword ? 'text' : 'password'}
                                            autoComplete="current-confirm-password"
                                            disabled={formDisabled}
                                            slotProps={{
                                                input: {
                                                    endAdornment: <PasswordAdornment show={showConfirmPassword} toggleShow={handleToggleConfirmPassword} />,
                                                },
                                            }}
                                        />
                                    </Box>
                                    <Box sx={{ mt: 2 }}>
                                        <Button
                                            fullWidth
                                            variant="contained"
                                            type="submit"
                                            disabled={!birthDate || !password || !confirmPassword || formDisabled}
                                            sx={{ mb: 1.5 }}
                                        >
                                            {formDisabled ? (
                                                <CircularProgress size={24} sx={{ color: blue[300] }} />
                                            ) : (
                                                'SAVE CHANGES'
                                            )}
                                        </Button>
                                        <Button variant="text" fullWidth>
                                            <Link
                                                to="/login"
                                                style={{
                                                    color: "#373737",
                                                    textDecoration: "none",
                                                    fontWeight: 500
                                                }}
                                            >
                                                GO BACK
                                            </Link>

                                        </Button>
                                    </Box>
                                </form>
                            </LocalizationProvider>
                        </Box>
                    </Box>

                </Grid>
            </Grid>
        </>
    );
}